import { isClientError } from '@lib/apiError'
import React from 'react'
import PublicErrorState from './PublicErrorState'
import PublicLoadingState from './PublicLoadingState'
import { Page } from './PublicShell.styles'
import PublicUnavailableState from './PublicUnavailableState'

/** The slice of a react-query result the gate branches on. */
interface PublicQueryState<T> {
	data: T | undefined
	isLoading: boolean
	error: unknown
}

interface PublicSectionGateProps<T> {
	/** The section's query state; multi-query pages combine theirs first. */
	query: PublicQueryState<T>
	/** e.g. "Loading ideas…". */
	loadingLabel: string
	/** e.g. "Ideas Unavailable". */
	unavailableTitle: string
	/** Per-page owner-intent wording ("…hasn't been shared publicly…"). */
	unavailableMessage: string
	/** Happy-path render, called only once data is present. */
	children: (data: T) => React.ReactElement
}

/**
 * The shared loading / unavailable / error gate for every public detail page.
 * 4xx (an unlisted trip or an OFF flag) → the owner-intent "unavailable" state;
 * 5xx/network → a generic error, so an outage never reads as "unshared".
 */
const PublicSectionGate = <T,>({
	query,
	loadingLabel,
	unavailableTitle,
	unavailableMessage,
	children,
}: PublicSectionGateProps<T>): React.ReactElement => {
	if (query.isLoading) {
		return (
			<Page>
				<PublicLoadingState label={loadingLabel} />
			</Page>
		)
	}

	if (query.error && !isClientError(query.error)) {
		return (
			<Page>
				<PublicErrorState />
			</Page>
		)
	}

	if (query.error || !query.data) {
		return (
			<Page>
				<PublicUnavailableState title={unavailableTitle} message={unavailableMessage} />
			</Page>
		)
	}

	return children(query.data)
}

export default PublicSectionGate
